import { mutate } from 'swr';
import { useMemo, useEffect, useCallback } from 'react';

import { useAuthenticatedAxios } from 'src/hooks/useAuthenticatedAxios';

import { authenticatedSwrOptions } from 'src/utils/constants';

import { zerreactorBackendEndpoints } from 'src/api/services/zerreactor_backend_definition/zerreactorBackendEndpoints';

import { useAuthContext } from 'src/auth/hooks';
import useAuthenticatedSWR from 'src/auth/hooks/use-authenticated-swr';

export function useProfileOperations() {
  const authenticatedAxios = useAuthenticatedAxios();
  const { checkUserSession } = useAuthContext();

  const updateSelfUser = useCallback(
    async (userId, userData) => {
      try {
        const response = await authenticatedAxios.put(
          zerreactorBackendEndpoints.User.UpdateSelfUser(),
          userData
        );
        // Update both the old User.GetById cache and new Auth.profile cache for backward compatibility
        mutate(zerreactorBackendEndpoints.User.GetById(userId), (currentData) => ({
          ...currentData,
          ...response.data,
        }));
        mutate(zerreactorBackendEndpoints.Auth.profile, (currentData) => ({
          ...currentData,
          ...response.data,
        }));

        return response;
      } catch (error) {
        console.error('Error updating user:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  const updatePassword = useCallback(
    async (userData) => {
      try {
        const response = await authenticatedAxios.put(
          zerreactorBackendEndpoints.Auth.updatePassword,
          userData
        );
        return response;
      } catch (error) {
        console.error('Error updating user:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  const updateProfileInformations = useCallback(
    async (userData) => {
      try {
        const response = await authenticatedAxios.put(
          zerreactorBackendEndpoints.Auth.updateProfileInformations,
          userData
        );

        // Update Auth.profile cache to reflect the changes immediately
        mutate(zerreactorBackendEndpoints.Auth.profile);

        // Refresh user session to update all components
        if (checkUserSession) {
          await checkUserSession();
        }

        return response;
      } catch (error) {
        console.error('Error updating user:', error);

        throw error;
      }
    },
    [authenticatedAxios, checkUserSession]
  );

  const updateProfilePicture = useCallback(
    async (formData) => {
      try {
        const response = await authenticatedAxios.put(
          zerreactorBackendEndpoints.Auth.profilePicture,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          }
        );

        // Update Auth.profile cache to reflect the profile picture changes immediately
        mutate(zerreactorBackendEndpoints.Auth.profile);

        // Refresh user session to update all components (including account drawer)
        if (checkUserSession) {
          await checkUserSession();
        }

        return response;
      } catch (error) {
        console.error('Error updating profile picture:', error);
        throw error;
      }
    },
    [authenticatedAxios, checkUserSession]
  );

  const requestEmailUpdate = useCallback(
    async (email) => {
      try {
        const response = await authenticatedAxios.put(
          zerreactorBackendEndpoints.Auth.requestEmailUpdate,
          {
            Email: email,
          }
        );
        return response;
      } catch (error) {
        console.error('Error requesting email update:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  const updateSelfEmail = useCallback(
    async (email, code) => {
      try {
        const response = await authenticatedAxios.put(
          zerreactorBackendEndpoints.Auth.updateSelfEmail,
          {
            Email: email,
            Code: code,
          }
        );
        return response;
      } catch (error) {
        console.error('Error updating email:', error);
        throw error;
      }
    },
    [authenticatedAxios]
  );

  return {
    updateProfileInformations,
    updateSelfUser,
    updatePassword,
    updateProfilePicture,
    requestEmailUpdate,
    updateSelfEmail,
  };
}

export function useGetUserById(userId) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.User.GetById(userId),
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      userSelf: data || {},
      userSelfLoading: isLoading,
      userSelfError: error,
      userSelfValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetProfile() {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.Auth.profile,
    authenticatedSwrOptions
  );

  // Update localStorage whenever profile data changes
  useEffect(() => {
    if (data && data.Id && data.FirstName) {
      // Only update if we have valid user data
      localStorage.setItem('userData', JSON.stringify(data));
    }
  }, [data]);

  return useMemo(
    () => ({
      userSelf: data || {},
      userSelfLoading: isLoading,
      userSelfError: error,
      userSelfValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}
export function useGetUserScheduleEvents(
  startDate = new Date(Date.now()).toISOString().split('T')[0],
  endDate = new Date(Date.now()).toISOString().split('T')[0]
) {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.Auth.scheduleEvents(startDate, endDate),
    authenticatedSwrOptions
  );

  return useMemo(
    () => ({
      scheduleEvents: data || [],
      scheduleEventsLoading: isLoading,
      scheduleEventsError: error,
      scheduleEventsValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}

export function useGetUserFeedbacks() {
  const { data, isLoading, error, isValidating } = useAuthenticatedSWR(
    zerreactorBackendEndpoints.Auth.feedbacks,
    authenticatedSwrOptions
  );

  console.log('Fetched feedbacks:', data);

  return useMemo(
    () => ({
      feedbacks: data || [],
      feedbacksLoading: isLoading,
      feedbacksError: error,
      feedbacksValidating: isValidating,
    }),
    [data, isLoading, error, isValidating]
  );
}
